/* capabilities.js — Lecture réellement testée sur cet appareil :
 * decodingInfo() par piste, canPlayType() pour le conteneur. L'identifiant de
 * codec est construit depuis les valeurs déclarées (H.264, HEVC, VP9, AV1, AAC,
 * Opus, MP3, FLAC) ; ailleurs, un motif d'absence. Les résultats sont des
 * valeurs mesurées. Expose self.MediaCapabilitiesProbe. */
'use strict';

var REASONS = {
  codec: 'codec {codec} non testable : son identifiant ne se construit pas depuis les valeurs déclarées',
  level: 'niveau {codec} non déclaré',
  profile: 'profil {codec} non reconnu ({profile})',
  rate: 'débit ou cadence non déclarés',
  api: 'ce navigateur ne propose pas le test de lecture (MediaCapabilities)'
};

function hex2(n) { return ('0' + n.toString(16).toUpperCase()).slice(-2); }
function num(v) { var n = parseFloat(String(v).replace(',', '.')); return isNaN(n) ? null : n; }
// « High@L4.1 » (anciens MediaInfo) ou Format_Level séparé
function level(t) {
  var l = t.formatLevel || (t.formatProfile && (t.formatProfile.match(/@L([\d.]+)/) || [])[1]);
  return num(l);
}
function profileName(t) { return t.formatProfile ? t.formatProfile.split('@')[0].trim() : null; }
function fail(key, p) { return { reason: REASONS[key].replace(/\{(\w+)\}/g, function (m, k) { return p[k] || m; }) }; }

var AVC_PROFILES = { 'Baseline': '42', 'Constrained Baseline': '42', 'Main': '4D', 'Extended': '58', 'High': '64', 'High 10': '6E', 'High 4:2:2': '7A', 'High 4:4:4 Predictive': 'F4' };
var HEVC_PROFILES = { 'Main': ['1', '6'], 'Main 10': ['2', '4'] };
var AV1_PROFILES = { 'Main': '0', 'High': '1', 'Professional': '2' };

/* Identifiant de codec et type MIME d'une piste, ou { reason }. */
function codecOf(t) {
  var f = t.formatRaw, lv, p = profileName(t);
  if (t.kind === 'video') {
    if (f === 'AVC') {
      if (!AVC_PROFILES[p]) return fail('profile', { codec: 'H.264', profile: p });
      if ((lv = level(t)) == null) return fail('level', { codec: 'H.264' });
      return { mime: 'video/mp4', codec: 'avc1.' + AVC_PROFILES[p] + (p === 'Constrained Baseline' ? '40' : '00') + hex2(Math.round(lv * 10)) };
    }
    if (f === 'HEVC') {
      if (!HEVC_PROFILES[p]) return fail('profile', { codec: 'HEVC', profile: p });
      if ((lv = level(t)) == null) return fail('level', { codec: 'HEVC' });
      var hp = HEVC_PROFILES[p];
      return { mime: 'video/mp4', codec: 'hvc1.' + hp[0] + '.' + hp[1] + '.' + (t.formatTier === 'High' ? 'H' : 'L') + Math.round(lv * 30) + '.B0' };
    }
    if (f === 'AV1') {
      if (AV1_PROFILES[p] == null) return fail('profile', { codec: 'AV1', profile: p });
      if ((lv = level(t)) == null) return fail('level', { codec: 'AV1' });
      var idx = (Math.floor(lv) - 2) * 4 + Math.round((lv % 1) * 10);
      return { mime: 'video/mp4', codec: 'av01.' + AV1_PROFILES[p] + '.' + ('0' + idx).slice(-2) + (t.formatTier === 'High' ? 'H' : 'M') + '.' + ('0' + (t.bitDepth || 8)).slice(-2) };
    }
    if (f === 'VP9') {
      if (p == null || !/^\d$/.test(p)) return fail('profile', { codec: 'VP9', profile: p });
      if ((lv = level(t)) == null) return fail('level', { codec: 'VP9' });
      return { mime: 'video/webm', codec: 'vp09.0' + p + '.' + ('0' + Math.round(lv * 10)).slice(-2) + '.' + ('0' + (t.bitDepth || 8)).slice(-2) };
    }
    return fail('codec', { codec: t.format || f || '?' });
  }
  if (f === 'AAC') {
    var feat = t.formatFeatures || 'LC';
    var ot = /PS/.test(feat) ? '29' : /SBR|HE/.test(feat) ? '5' : '2';
    return { mime: 'audio/mp4', codec: 'mp4a.40.' + ot };
  }
  if (f === 'Opus') return { mime: 'audio/webm', codec: 'opus' };
  if (f === 'MPEG Audio' && /Layer 3/.test(t.formatProfile || '')) return { mime: 'audio/mpeg', codec: null };
  if (f === 'FLAC') return { mime: 'audio/flac', codec: null };
  return fail('codec', { codec: t.format || f || '?' });
}

// Configuration MediaCapabilities d'une piste, ou { reason }.
function configOf(t, container) {
  var c = codecOf(t);
  if (c.reason) return c;
  var contentType = c.mime + (c.codec ? '; codecs="' + c.codec + '"' : '');
  if (t.kind === 'video') {
    var bitrate = t.bitRate || (container && container.overallBitRate);
    if (!bitrate || !t.frameRate || !t.width || !t.height) return fail('rate', {});
    var video = { contentType: contentType, width: t.width, height: t.height, bitrate: Math.round(bitrate), framerate: t.frameRate };
    var tr = t.transferCharacteristics || '';
    if (/PQ|2084/.test(tr)) video.transferFunction = 'pq';
    else if (/HLG/.test(tr)) video.transferFunction = 'hlg';
    if (/2020/.test(t.colorPrimaries || '')) video.colorGamut = 'rec2020';
    return { type: 'file', video: video };
  }
  var audio = { contentType: contentType };
  if (t.channels) audio.channels = String(t.channels);
  if (t.bitRate) audio.bitrate = Math.round(t.bitRate);
  if (t.samplingRate) audio.samplerate = t.samplingRate;
  return { type: 'file', audio: audio };
}

var CONTAINER_MIME = {
  'MPEG-4': ['video/mp4', 'audio/mp4'], 'QuickTime': ['video/quicktime', 'video/quicktime'],
  'Matroska': ['video/x-matroska', 'audio/x-matroska'], 'WebM': ['video/webm', 'audio/webm'],
  'MPEG Audio': ['audio/mpeg', 'audio/mpeg'], 'FLAC': ['audio/flac', 'audio/flac'],
  'Wave': ['audio/wav', 'audio/wav'], 'Ogg': ['video/ogg', 'audio/ogg']
};

function measured(subject, property, value, detail) {
  var v = { subject: subject, property: property, value: value, origin: 'measured', source: 'navigateur' };
  if (detail) v.detail = detail;
  return v;
}
function subjectOf(t) { return { kind: t.kind, ordinal: t.ordinal, id: t.id != null ? t.id : null }; }

/* -> { values, absent: [{ subject, property, reason }] }.
 * env : { mediaCapabilities, canPlayType } — le navigateur, ou des doubles. */
function probe(model, env) {
  var values = [], absent = [];
  var FILE = { kind: 'container' };
  var hasVideo = model.tracks.some(function (t) { return t.kind === 'video'; });
  var mimes = CONTAINER_MIME[model.container.formatRaw];
  if (mimes && env.canPlayType) {
    var mime = mimes[hasVideo ? 0 : 1];
    var answer = env.canPlayType(mime);
    values.push(measured(FILE, 'browserContainer', answer !== '', mime + ' : ' + (answer || 'non')));
  } else absent.push({ subject: FILE, property: 'browserContainer', reason: fail('codec', { codec: model.container.format || '?' }).reason });

  var mc = env.mediaCapabilities;
  var tracks = model.tracks.filter(function (t) { return t.kind === 'video' || t.kind === 'audio'; });
  var jobs = tracks.map(function (t) {
    var cfg = configOf(t, model.container);
    if (cfg.reason) { absent.push({ subject: subjectOf(t), property: 'browserDecode', reason: cfg.reason }); return null; }
    if (!mc || !mc.decodingInfo) { absent.push({ subject: subjectOf(t), property: 'browserDecode', reason: REASONS.api }); return null; }
    var ct = (cfg.video || cfg.audio).contentType;
    return mc.decodingInfo(cfg).then(function (r) {
      values.push(measured(subjectOf(t), 'browserDecode', !!r.supported, ct));
      if (r.supported) values.push(measured(subjectOf(t), 'browserSmooth', !!r.smooth, ct));
    }, function (err) {
      absent.push({ subject: subjectOf(t), property: 'browserDecode', reason: String(err && err.message || err) });
    });
  });
  return Promise.all(jobs).then(function () { return { values: values, absent: absent }; });
}

var api = { probe: probe, codecOf: codecOf, configOf: configOf, REASONS: REASONS };
globalThis.MediaCapabilitiesProbe = api;
export default api;
